<template>
    <vx-table-row :data-test="`term-row-${props.term.id}`">
        <vx-table-row-title>
            <span class="text-xs sm:text-sm wrap-break-word max-w-[200px]">{{ props.term.title }}</span>
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label>Status:</template>
            <term-status-badge :status="props.term.status" />
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label>Effective At:</template>
            {{ formattedEffectiveAt }}
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label>Creator:</template>
            {{ props.term.creator?.name || '-' }}
        </vx-table-row-item>

        <vx-table-row-buttons>
            <vx-dropdown v-if="props.term.status !== 'archived'" placement="right">
                <vx-button
                    type="button"
                    color="muted"
                    outline
                    class="w-[45px]"
                    :data-test="`menu-${props.term.id}`"
                    aria-label="Term actions"
                >
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item @click="acceptancesVisible = true" :data-test="`acceptances-${props.term.id}`">
                        Acceptances
                        <font-awesome-icon widthAuto :icon="faUsers" />
                    </vx-dropdown-item>

                    <vx-dropdown-item
                        v-if="props.term.status === 'published'"
                        :data-test="`pause-${props.term.id}`"
                        @click="emit('pause', props.term)"
                    >
                        Pause <font-awesome-icon widthAuto :icon="faCirclePause" />
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="props.term.status === 'paused'"
                        :data-test="`resume-${props.term.id}`"
                        @click="emit('resume', props.term)"
                    >
                        Resume <font-awesome-icon widthAuto :icon="faCirclePlay" />
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="props.term.status === 'published' || props.term.status === 'paused'"
                        :data-test="`archive-${props.term.id}`"
                        @click="emit('archive', props.term)"
                    >
                        Archive <font-awesome-icon widthAuto :icon="faBoxArchive" />
                    </vx-dropdown-item>
                    <vx-dropdown-item
                        v-if="props.term.status === 'draft'"
                        :data-test="`delete-${props.term.id}`"
                        @click="emit('delete', props.term)"
                    >
                        Delete <font-awesome-icon widthAuto :icon="faTrash" />
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                v-if="props.term.status === 'draft'"
                :href="editHref"
                type="button"
                color="muted"
                outline
                aria-label="Edit"
                :data-test="`edit-${props.term.id}`"
            >
                Edit
                <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>

            <vx-button
                v-else
                :href="editHref"
                type="button"
                color="muted"
                outline
                aria-label="View"
                :data-test="`view-${props.term.id}`"
            >
                View
                <font-awesome-icon widthAuto :icon="faEye" />
            </vx-button>
        </vx-table-row-buttons>

        <term-acceptances-modal v-model:visible="acceptancesVisible" :teamId="props.teamId" :term="props.term" />
    </vx-table-row>
</template>

<script lang="ts" setup>
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faBoxArchive,
    faCirclePause,
    faCirclePlay,
    faEllipsis,
    faEye,
    faFilePen,
    faTrash,
    faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { computed, ref } from 'vue';
import { shortDateTime } from '~/utils/date';
import type { Term } from '~/services/adminTermsClient.types';
import TermAcceptancesModal from './TermAcceptancesModal.vue';
import TermStatusBadge from './TermStatusBadge.vue';

const props = defineProps<{
    teamId: number;
    term: Term;
}>();

const emit = defineEmits<{
    delete: [term: Term];
    pause: [term: Term];
    resume: [term: Term];
    archive: [term: Term];
}>();

const acceptancesVisible = ref(false);

const formattedEffectiveAt = computed(() => shortDateTime(props.term.effective_at));

const editHref = computed(() => `/kiosk/teams/${props.teamId}/terms/${props.term.id}`);
</script>
